import './AdvancedDashboard.scss'
import React, {useEffect, useState} from "react";
import {observer} from "mobx-react-lite";
import {useStores} from "../../stores";
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    Grid,
    IconButton,
    InputLabel,
    Switch,
    Tooltip,
    Typography
} from "@mui/material";
import {routes} from "../../routing/routes";
import {CurrencyComponent} from "../../components/currency-component/CurrencyComponent";
import {generatePath, useNavigate} from "react-router";
import {TreeMap} from "../../components/visualization/tree-map/TreeMap";
import {BubbleMap} from "../../components/visualization/bubble-map/BubbleMap";
import {LineChart} from "../../components/visualization/line-chart/LineChart";
import {AdvancedTable} from '../../components/data-table/AdvancedTable';
import {environment} from '../../env';
import {AdvancedDashboardHeaderFilters} from "./AdvancedDashboardHeaderFilters";
import {DonutChart} from "../../components/visualization/donut-chart/DonutChart";
import {AdvancedBarChart} from "../../components/visualization/bar-chart/AdvancedBarChart";
import {runInAction} from "mobx";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import {Refresh} from "@mui/icons-material";
import {DownloadDataButton} from "../../components/download-data-button/DownloadDataButton";
import {buPivotExcelColumns, BuPivotHeaders, excelColumns, OpportunityHeaders, SupBreakdownHeaders} from "./collumns";
import {analyticsExportEvent} from "../../services/analytics/AnalyticEvents";
import type {ExcelDataColumns} from "../../services/ApiTypes";
import {downloadAsFile} from "../../components/download-data-button/DownloadAsFile";
import {debug} from "../../utils/js-utils";
import {MultiSelection} from "../../components/visualization/api-dropdown-filter/ApiDropdownTypes";
import {MainPageFacade} from "../../components/main/page/MainPageFacade.tsx";
import type {KpiBoxedValue} from "../../components/kpi-boxed-values/KpiBoxedValues.tsx";
import {KpiBoxedValues} from "../../components/kpi-boxed-values/KpiBoxedValues.tsx";
import {CARD_CONFIG} from "../../components/dashboard/DashboardCardConfigs.tsx";
import LoadingBarStore from "../../stores/LoadingBarStore.tsx";

export const AdvancedDashboard: React.FC = observer(() => {
    const {
        authStore,
        p,
        bagStore,
        businessCaseGeneratorStore,
        synergyStore,
        materializedApi
    } = useStores();
    const navigate = useNavigate();
    const databagId = bagStore.bagId;
    const showL4 = Boolean(p.p.showL4InOpportunityDashboard);
    const hideParents = Boolean(p.p.hideParentSupplierOpportunityDashboard);
    // TODO CAT-2195: This would be better configured by a field in databag or synergy model,
    //  that would specify if user is allowed to split the advanced dashboard

    const [isLoading, setIsLoading] = useState(false);

    // Check for a requested databag id change
    useEffect(() => {
        bagStore.advanced.setDatabagId(databagId);
    }, [databagId, bagStore.advanced])

    const isReady = Boolean(bagStore.advanced.kpi ?? false) && !bagStore.advanced.isLoading;
    useEffect(() => {
        if (!isReady) {
            LoadingBarStore.start(true);
        } else {
            LoadingBarStore.setComplete()
        }
    }, [isReady])

    useEffect(() => {
        runInAction(() => {
            businessCaseGeneratorStore.hasLoaded = false;
            businessCaseGeneratorStore.risksHaveLoaded = false;
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const OPPORTUNITY_HEADERS = OpportunityHeaders(bagStore.advanced, showL4);
    const SUP_BREAKDOWN_HEADERS = SupBreakdownHeaders(bagStore.advanced, p, showL4, hideParents, synergyStore);
    const BUSINESS_UNIT_PIVOT_HEADERS = BuPivotHeaders(bagStore.advanced, p, showL4, hideParents, synergyStore);

    const handleDownloadSupBreakdownData = () => {
        analyticsExportEvent({
            export_type: 'adv_dash_supplies_excel',
            object_key: String(databagId),
            user: authStore.email
        })
        const dataName = bagStore.bag?.name || `Dataset ${databagId}`;
        const filename = `${dataName}.xlsx`;
        const columns: ExcelDataColumns[] = excelColumns(bagStore.advanced, p, showL4, hideParents, synergyStore);
        // const ordering = bagStore.advanced.sup_breakdown_ordering;
        const ordering = '';
        const url = window.location.href;
        return materializedApi.downloadInsightsExcel(databagId, filename, columns, bagStore.advanced.filterQueryParams, ordering, url)
            .then(resp => downloadAsFile(resp, filename))
    };

    const handleDownloadBuPivotData = () => {
        analyticsExportEvent({
            export_type: 'adv_dash_bu_pivot_excel',
            object_key: String(databagId),
            user: authStore.email
        })
        const dataName = bagStore.bag?.name || `Dataset ${databagId}`;
        const filename = `${dataName} ${p.buDataName}.xlsx`;
        const columns: ExcelDataColumns[] = buPivotExcelColumns(bagStore.advanced, p, showL4, hideParents, synergyStore);
        return materializedApi.downloadAdvancedBusinessUnitPivot(databagId, filename, columns, bagStore.advanced.filterQueryParams, '', window.location.href)
            .then(resp => downloadAsFile(resp, filename))
    };

    // We show the parent supplier capabilities if the group_by_parent_supplier filter is defined
    const hasPsResults = Boolean((bagStore.advanced.filters.group_by_parent_supplier ?? null) !== null);

    // TODO: Do we want to influence this behavior by config?
    const enableParentSupplierSwitch = hasPsResults;
    const enableCommonCategorySwitch = hasPsResults && synergyStore.isSynergyDatabag && !p.p.disableCommonCategoryAdvDashboard;
    const enableCommonSupplierSwitch = hasPsResults && synergyStore.isSynergyDatabag && !p.p.disableCommonSuppliersAdvDashboard;

    const _Parent_Supplier = p.p.parentSupplierAltName ?? 'Parent Supplier';
    const _parent_supplier = _Parent_Supplier.toLowerCase();

    const ByParentSupplierSwitchComponent = <FormGroup className="advanced-dashboard-switch">
        <FormControlLabel
            control={<Switch
                checked={bagStore.advanced.groupParentSuppliersFilter}
                onChange={(_e, checked: boolean) => bagStore.advanced.groupParentSuppliersFilter = checked}
            />}
            label={<Typography variant="caption" className="">
                By {_parent_supplier}
            </Typography>}
        />
    </FormGroup>
    const JustParentSupplierSwitchComponent = <FormGroup className="advanced-dashboard-switch">
        <FormControlLabel
            control={<Switch
                checked={bagStore.advanced.groupParentSuppliersFilter}
                onChange={(_e, checked: boolean) => bagStore.advanced.groupParentSuppliersFilter = checked}
            />}
            label={<Typography variant="caption" className="">
                {_Parent_Supplier}
            </Typography>}
        />
    </FormGroup>
    const OnlyCommonCategoriesSwitchComponent = <FormGroup className="advanced-dashboard-switch">
        <FormControlLabel
            control={<Switch
                checked={bagStore.advanced.filters.only_common_categories ?? false}
                onChange={(_e, checked: boolean) => bagStore.advanced.setOnlyCommonCategoriesFilter(checked)}
            />}
            label={<Typography variant="caption" className="">
                Only common categories
            </Typography>}
        />
    </FormGroup>
    const OnlyCommonSuppliersSwitchComponent = <FormGroup className="advanced-dashboard-switch">
        <FormControlLabel
            control={<Switch
                checked={bagStore.advanced.filters.only_common_suppliers ?? false}
                onChange={(_e, checked: boolean) => bagStore.advanced.setOnlyCommonSuppliersFilter(checked)}
            />}
            label={<Typography variant="caption" className="">
                Only common suppliers
            </Typography>}
        />
    </FormGroup>

    const kpiData = bagStore.advanced.kpi;
    const kpiValues = React.useMemo<KpiBoxedValue[]>(() => [
        {
            title: 'Categories',
            subtitle: kpiData ? (kpiData.n_categories || '0') : '-',
        }, {
            title: 'Spend',
            subtitle: kpiData ? <CurrencyComponent v={kpiData.total_spend}/> : '-',
        }, {
            title: 'Suppliers',
            subtitle: kpiData ? (kpiData.n_suppliers || '0') : '-',
        }, {
            title: 'Transactions',
            subtitle: kpiData ? (kpiData.total_transactions || '0') : '-',
        },
    ], [kpiData]);


    return (
        <MainPageFacade
            title={CARD_CONFIG['adv-dashboard'].title}
            actionBarStart={<>
                {p.opportunitySplitAllowed && synergyStore.isSynergyDatabag &&
                    <Tooltip title={bagStore.advanced.synergySplited ?
                        'These are comparison between sources of synergy file, you can combine them for overview.' :
                        'This is overview of synergy file, you can split it to compare sources.'}>
                        <span>
                            <Button variant="outlined" className="mx-4" onClick={() => {
                            bagStore.advanced.setSynergySplited(!bagStore.advanced.synergySplited);
                        }}>
                                {bagStore.advanced.synergySplited ? 'Combine' : 'Split'}
                            </Button>
                        </span>
                    </Tooltip>
                }
            </>}
            actionBarEnd={<>
                <Tooltip title="Refresh data">
                    <span>
                        <IconButton
                        size="small"
                        aria-label="refresh"
                        disabled={bagStore.advanced_job.already_running ?? true}
                        onClick={() => bagStore.advanced_job.createRecalculation()}
                        className="mx-2">
                            <Refresh/>
                        </IconButton>
                    </span>
                </Tooltip>
            </>}
            className="advanced-dashboard-page">


            <Grid container>
                <Grid size={1}></Grid>
                <Grid size={10}>
                    <KpiBoxedValues values={kpiValues}/>
                    {bagStore.advanced_job.already_running && <Grid container className="mt-4">
                        <Grid size={12}>
                            <Alert severity="warning" className="advanced-dashboard-alert mx-auto">
                                <AlertTitle>
                                    A data refresh is still being processed
                                </AlertTitle>
                                This page might not be up to date.
                                {/*Estimated completion time: */}
                            </Alert>
                        </Grid>
                    </Grid>}
                    {environment.isTest && <pre
                        // Debug info
                        style={{
                            maxWidth: '500px',
                            marginLeft: 'auto',
                            marginRight: 'auto',
                            height: '10em',
                            maxHeight: '10em'
                        }}>
                        databag_id: {'' + databagId}, hasPsResults={'' + hasPsResults}, bag.snr={'' + bagStore.bag?.has_supplier_normalization_results}<br/>
                        isSynergyDatabag={'' + bagStore.synergy.isSynergyDatabag}<br/>
                        {debug(bagStore.advanced.filterQueryParams)}
                    </pre>}

                    <AdvancedDashboardHeaderFilters showL4={showL4}/>

                    {p.p.demoFakeBusinessCaseGenerator && MultiSelection.isSet(bagStore.advanced.filters.category_l1) && bagStore.advanced.filters.category_l234 && (
                        <Alert
                            severity="info"
                            action={
                                <>
                                    <div style={{display: 'flex', alignItems: 'baseline'}}>
                                        <FormControl variant="standard" style={{width: '20em', marginRight: '1em'}}>
                                            <InputLabel id="business-case-dropdown-label">Select Strategy</InputLabel>
                                            <Select
                                                labelId="business-case-dropdown-label"
                                                id="business-case-dropdown"
                                                label="Select Strategy"
                                                onChange={(event) => {
                                                    runInAction(() => {
                                                        businessCaseGeneratorStore.selectedStrategy = event.target.value;
                                                    });
                                                }}
                                            >
                                                <MenuItem value={1}>Demand / supplier consolidation</MenuItem>
                                                <MenuItem value={2}>Time to market ( TTM ) optimization</MenuItem>
                                                <MenuItem value={3}>Tail spend reduction</MenuItem>
                                                <MenuItem value={4}>Optimize cash flow</MenuItem>
                                                <MenuItem value={5}>Price variation optimization</MenuItem>
                                                <MenuItem value={6}>Multi - source strategy</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <Button
                                            style={{width: '15em'}}
                                            disabled={!businessCaseGeneratorStore.selectedStrategy || isLoading}
                                            onClick={() => {
                                                setIsLoading(true);
                                                // Simulate a delay for 1.8 seconds and perform your actions
                                                setTimeout(() => {
                                                    runInAction(() => {
                                                        businessCaseGeneratorStore.hasLoaded = false;
                                                        businessCaseGeneratorStore.pageController.navigateToPage('review-opportunities');
                                                    });
                                                    navigate(generatePath(routes.demo_business_case_report, {id: bagStore.bagId}));
                                                    setIsLoading(false);
                                                }, 1800);
                                            }}
                                        >
                                            {isLoading ? 'Generating...' : 'Generate Business Case'}
                                        </Button>
                                    </div>
                                </>
                            }
                        >
                            {/*<AlertTitle>You can use Gen AI to build your Business case</AlertTitle>*/}
                            {/*Based on the data in this dashboard, you can generate a business case.*/}
                            <AlertTitle>Select scope and defined category strategy to build your business
                                case</AlertTitle>
                            Based on this selection, you can use Mithra Ai assistant to kick start a business case
                        </Alert>
                    )}

                    <Grid container>
                        {!bagStore.advanced.synergySplited && <Grid container>
                            <Grid className="advanced-dashboard-chart" size={6}>
                                {bagStore.advanced.hasGeoRegionFilter
                                    ? <BubbleMap
                                        data={bagStore.advanced.geoRegionBubbleData}
                                        selectedIds={bagStore.advanced.geoRegionFilterIds}
                                        onSelectedChanged={bagStore.advanced.geoRegionFilterChangedByBubbleMap}
                                    />
                                    : <BubbleMap
                                        data={bagStore.advanced.countryBubbleData}
                                        selectedIds={bagStore.advanced.filters.country}
                                        onSelectedChanged={bagStore.advanced.countryFilterChanged}
                                    />
                                }
                            </Grid>
                            <Grid className="advanced-dashboard-chart" size={6}>
                                {/*treeData: {'' + bagStore.advanced.treeData.children.length}*/}
                                <TreeMap
                                    data={bagStore.advanced.treeData}
                                    selectedCategory={bagStore.advanced.singleSelectedCategory ?? null}
                                    categoryFilterChanged={bagStore.advanced.onTreeMapCategoryFilterChanged}
                                />
                            </Grid>
                            <Grid className="advanced-dashboard-chart" size={6}>
                                <Card className="advanced-dashboard-chart-card">
                                    <CardContent>
                                        <Typography variant={'h6'} className={'advanced-dashboard-title'}>
                                            Spend breakdown
                                            {enableParentSupplierSwitch && ByParentSupplierSwitchComponent}
                                        </Typography>
                                        <Typography sx={{fontSize: 14}} color="text secondary" gutterBottom
                                                    className={'advanced-dashboard-subtitle'}>
                                            Spend distribution by
                                            top {bagStore.advanced.groupParentSuppliersFilter ? (_parent_supplier + 's') : 'suppliers'}
                                        </Typography>
                                        <Divider className="mb-5"/>
                                        <DonutChart
                                            data={bagStore.advanced.spendDonutData}
                                            loading={bagStore.advanced.advancedSpendDonutDataRM.busy}
                                            selection={bagStore.advanced.groupParentSuppliersFilter ? bagStore.advanced.filters.sp_ids : bagStore.advanced.filters.s_ids}
                                            onSelectionChange={bagStore.advanced.groupParentSuppliersFilter ? bagStore.advanced.parentSupplierFilterChanged : bagStore.advanced.supplierFilterChanged}
                                        />
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid className="advanced-dashboard-chart" size={6}>
                                <Card className="advanced-dashboard-chart-card">
                                    <CardContent>
                                        <Typography variant={'h6'} className={'advanced-dashboard-title'}>
                                            Supplier spend
                                            {enableParentSupplierSwitch && ByParentSupplierSwitchComponent}
                                        </Typography>
                                        <Typography sx={{fontSize: 14}} color="text secondary" gutterBottom
                                                    className={'advanced-dashboard-subtitle'}>
                                            Total spend distribution per
                                            {' '}{bagStore.advanced.groupParentSuppliersFilter ? (_parent_supplier + 's') : 'suppliers'}
                                        </Typography>
                                        <Divider/>
                                        <AdvancedBarChart
                                            dataWrapper={bagStore.advanced.spendConcentrationData}
                                            loading={bagStore.advanced.advancedSpendConcentrationDataRM.busy}
                                            selection={bagStore.advanced.groupParentSuppliersFilter ? bagStore.advanced.filters.sp_ids : bagStore.advanced.filters.s_ids}
                                            onSelectionChange={bagStore.advanced.groupParentSuppliersFilter ? bagStore.advanced.parentSupplierFilterChanged : bagStore.advanced.supplierFilterChanged}
                                        />
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>}

                        {bagStore.advanced.synergySplited && <Grid container>
                            {bagStore.synergy.srcDatabagIds?.map((databagId, index) =>
                                <Grid key={index} size={12 / (bagStore.synergy.srcDatabagIds?.length ?? 1)}>
                                    <Typography variant="h5" component="p"
                                                style={{textAlign: 'center', wordBreak: 'break-word'}}>
                                        {bagStore.getBagName(databagId)}
                                    </Typography>
                                </Grid>
                            )}
                            {/*TODO CAT-2195: The limitation here is that number of sources must be less than or equal to 4*/}
                            {bagStore.advanced.treeDataSplited.map((atd, index) =>
                                <Grid
                                    key={index}
                                    className="advanced-dashboard-chart"
                                    size={12 / bagStore.advanced.treeDataSplited.length}>
                                    <TreeMap
                                        data={atd}
                                        selectedCategory={bagStore.advanced.singleSelectedCategory ?? null}
                                        categoryFilterChanged={bagStore.advanced.onTreeMapCategoryFilterChanged}
                                    />
                                </Grid>)}
                            {bagStore.advanced.spendDonutDataSplited.map((donutChartSupplierSpendData, index) =>
                                <Grid
                                    key={index}
                                    className="advanced-dashboard-chart"
                                    size={12 / bagStore.advanced.spendDonutDataSplited.length}>
                                    <Card className="advanced-dashboard-chart-card">
                                        <CardContent>
                                            <Typography variant={'h6'} className={'advanced-dashboard-title'}>
                                                Spend breakdown
                                                {enableParentSupplierSwitch && JustParentSupplierSwitchComponent}
                                            </Typography>
                                            <Typography sx={{fontSize: 14}} color="text secondary" gutterBottom
                                                        className={'advanced-dashboard-subtitle'}>
                                                Spend distribution by top suppliers
                                            </Typography>
                                            <Divider/>
                                            <DonutChart
                                                data={donutChartSupplierSpendData}
                                                loading={bagStore.advanced.advancedSpendDonutDataSplitedRMs[index].busy}
                                                selection={bagStore.advanced.groupParentSuppliersFilter ? bagStore.advanced.filters.sp_ids : bagStore.advanced.filters.s_ids}
                                                onSelectionChange={bagStore.advanced.groupParentSuppliersFilter ? bagStore.advanced.parentSupplierFilterChanged : bagStore.advanced.supplierFilterChanged}
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>)}
                            {bagStore.advanced.spendConcentrationDataSplited.map((asd, index) =>
                                <Grid
                                    key={index}
                                    className="advanced-dashboard-chart"
                                    size={12 / bagStore.advanced.spendDonutDataSplited.length}>
                                    <Card className="advanced-dashboard-chart-card">
                                        <CardContent>
                                            <Typography variant={'h6'} className={'advanced-dashboard-title'}>
                                                Supplier spend
                                                {enableParentSupplierSwitch && JustParentSupplierSwitchComponent}
                                            </Typography>
                                            <Typography sx={{fontSize: 14}} color="text secondary" gutterBottom
                                                        className={'advanced-dashboard-subtitle'}>
                                                Total spend distribution per supplier
                                            </Typography>
                                            <Divider/>
                                            <AdvancedBarChart
                                                dataWrapper={asd}
                                                columnBreakDown={bagStore.advanced.spendDonutDataSplited.length}
                                                loading={bagStore.advanced.advancedSpendConcentrationDataSplitedRMs[index].busy}
                                                selection={bagStore.advanced.groupParentSuppliersFilter ? bagStore.advanced.filters.sp_ids : bagStore.advanced.filters.s_ids}
                                                onSelectionChange={bagStore.advanced.groupParentSuppliersFilter ? bagStore.advanced.parentSupplierFilterChanged : bagStore.advanced.supplierFilterChanged}
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>)}
                            <Grid className="advanced-dashboard-chart full-width-bubble-map" size={12}>
                                {/*TODO: CAT-2894: Also make a Region based geoMap*/}
                                <BubbleMap
                                    data={bagStore.advanced.geoBubbleDataSplited}
                                    selectedIds={bagStore.advanced.filters.country}
                                    onSelectedChanged={bagStore.advanced.hasGeoRegionFilter ? undefined : bagStore.advanced.countryFilterChanged}
                                />
                            </Grid>
                        </Grid>}
                    </Grid>

                    <section className="advanced-looker-tables">
                        <Typography variant={"h4"} className="advanced-dashboard-title my-3">
                            Category specific opportunities
                            {enableCommonCategorySwitch && OnlyCommonCategoriesSwitchComponent}
                        </Typography>
                        <AdvancedTable
                            className="advanced-opportunity-table"
                            headers={OPPORTUNITY_HEADERS}
                            requestManager={bagStore.advanced.advancedOpportunityDataRM}
                            page={bagStore.advanced.advancedOpportunityDataPage}
                            onPageChange={bagStore.advanced.setAdvancedOpportunityDataPage}
                            perPage={bagStore.advanced.advancedOpportunityDataPerPage}
                            onPerPageChange={bagStore.advanced.setAdvancedOpportunityDataPerPage}
                            breakdownFilterChanged={bagStore.advanced.breakdownFilterChanged}
                            ordering={bagStore.advanced.opportunity_ordering}
                            // TODO CAT-2266: Pivot should be on customized columns
                            // rowSpan
                        />

                        {bagStore.configStore.customerConfig?.get('ADV_DASH_GRAPHS') &&
                            <section className="advanced-line-graph-container">
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6">
                                            Spend per supplier
                                        </Typography>
                                        <Typography sx={{fontSize: 14}} color="text.secondary" gutterBottom
                                                    className="advanced-dashboard-subtitle">
                                            Analyzing trends in various categories
                                        </Typography>
                                        <Divider className="my-1"/>
                                        <Box mt={2}>
                                            <LineChart dataWrapper={bagStore.advanced.spendPerSupplierData}/>
                                        </Box>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6">
                                            Spend per level one category
                                        </Typography>
                                        <Typography sx={{fontSize: 14}} color="text.secondary" gutterBottom
                                                    className={'advanced-dashboard-subtitle'}>
                                            Analyzing trends in specific levels
                                        </Typography>
                                        <Divider className="my-1"/>
                                        <Box mt={2}>
                                            <LineChart dataWrapper={bagStore.advanced.spendPerActiveL1Data}/>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </section>
                        }

                        <div className="table-section">
                            <Grid container justifyContent="start" alignItems="center">
                                <Grid>
                                    <Typography variant="h4" className="advanced-dashboard-title my-3">
                                        Supplier and spend category overview
                                    </Typography>
                                </Grid>
                                {enableCommonSupplierSwitch && <Grid>{OnlyCommonSuppliersSwitchComponent}</Grid>}
                                {enableParentSupplierSwitch && <Grid>{ByParentSupplierSwitchComponent}</Grid>}
                                <Grid flexGrow={1}/>
                                <Grid>
                                    <DownloadDataButton
                                        dataLoading={bagStore.advanced.advancedSupplierBreakdownDataRM.busy}
                                        dataSize={bagStore.advanced.advancedSupplierBreakdownDataRM.result?.data.count ?? 0}
                                        onClick={handleDownloadSupBreakdownData}
                                    />
                                </Grid>
                            </Grid>

                            <AdvancedTable
                                className="advanced-supplier-breakdown-table"
                                headers={SUP_BREAKDOWN_HEADERS}
                                requestManager={bagStore.advanced.advancedSupplierBreakdownDataRM}
                                page={bagStore.advanced.advancedSupplierBreakdownPage}
                                onPageChange={bagStore.advanced.setAdvancedSupplierBreakdownPage}
                                perPage={bagStore.advanced.advancedSupplierBreakdownPerPage}
                                onPerPageChange={bagStore.advanced.setAdvancedSupplierBreakdownPerPage}
                                breakdownFilterChanged={bagStore.advanced.breakdownFilterChanged}
                                ordering={bagStore.advanced.sup_breakdown_ordering}
                                // TODO CAT-2266: Pivot should be on customized columns
                                // rowSpan
                            />
                        </div>

                        {p.p.synergyDashboardShowBusinessUnits &&
                            <div className="table-section">

                                <Grid container justifyContent="start" alignItems="center">
                                    <Grid>
                                        <Typography variant="h4" className="advanced-dashboard-title my-3">
                                            {p.buDataName} overview
                                        </Typography>
                                    </Grid>
                                    {enableParentSupplierSwitch && <Grid>{ByParentSupplierSwitchComponent}</Grid>}
                                    <Grid flexGrow={1}/>
                                    <Grid>
                                        <DownloadDataButton
                                            dataLoading={bagStore.advanced.advancedBusinessUnitPivotDataRM.busy}
                                            dataSize={bagStore.advanced.advancedBusinessUnitPivotDataRM.result?.data.count ?? 0}
                                            onClick={handleDownloadBuPivotData}
                                        />
                                    </Grid>
                                </Grid>

                                <AdvancedTable
                                    className="business-unit-pivot-table"
                                    headers={BUSINESS_UNIT_PIVOT_HEADERS}
                                    requestManager={bagStore.advanced.advancedBusinessUnitPivotDataRM}
                                    page={bagStore.advanced.advancedBusinessUnitPivotDataPage}
                                    onPageChange={bagStore.advanced.setBusinessUnitPivotDataPage}
                                    perPage={bagStore.advanced.advancedBusinessUnitPivotDataPerPage}
                                    onPerPageChange={bagStore.advanced.setBusinessUnitPivotDataPerPage}
                                    breakdownFilterChanged={bagStore.advanced.breakdownFilterChanged}
                                    ordering={bagStore.advanced.bu_pivot_ordering}
                                />

                            </div>
                        }

                    </section>
                </Grid>
                <Grid size={1}></Grid>
            </Grid>
        </MainPageFacade>
    );
});
